import styled from 'styled-components';

import ButtonPattern from '@mlb/react-patterns/v4/components/Button';
import IconPattern from '@mlb/react-patterns/v4/components/Icon';
import { IconWrapper } from '@mlb/react-patterns/v4/components/Icon/icon.style';
import { ButtonShapes, ButtonSizes } from '@mlb/react-patterns/v4/components/Button/button.types';
import { Label } from '@mlb/react-patterns/v4/components/Button/button.style';
import { ACTION } from './components/WinnerThumbnail';
import GDBREAKPOINTS from '../themes/breakpoints';
import Skeleton from '@mlb/react-patterns/v4/components/Skeleton/skeleton';

const { SM_REG_MAX } = GDBREAKPOINTS;

export const WINNERS = 'winners';
export const VOTING = 'voting';
export const Title = styled.div`
    font-size: 24px;
    font-weight: bold;
    font-family: var(--gameday-primary-font);
    margin-bottom: var(--gameday-section-margin-sm);
`;

export const Picture = styled.picture`
    display: block;
    margin: 0 auto var(--gameday-section-margin-sm);
    width: 100%;
    max-width: 250px;
    max-height: 130px;
    aspect-ratio: auto 250/130;
`;

export const Img = styled.img`
    width: 100%;
`;

export const BaseVoteLink = styled(ButtonPattern).attrs(props => ({
    shape: ButtonShapes.PILL,
    size: ButtonSizes.XLARGE,
    iconName: 'arrow-next',
    iconLeft: false,
    additionalProps: {
        as: 'a',
        role: undefined,
        type: undefined,
        'aria-pressed': undefined,
        color: undefined,
        shape: undefined,
        target: '_blank',
        href: props.href || ''
    }
}))`
    // TODO: move all these react-patterns Button pattern overrides to a reusable Button component that extends the patterns component
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    font-size: 16px;
    border: 1px solid var(--gameday-button-hollow-border);
    text-decoration: none;
    border-radius: 24px;
    ${Label} {
        color: var(--gameday-button-hollow-text);
    }
    ${IconWrapper} {
        width: 14px;
        height: 14px;
        padding-top: unset;
        fill: var(--gameday-button-hollow-border);
        path {
            fill: var(--gameday-button-hollow-border);
        }
    }
    &:hover {
        background: var(--gameday-button-hollow-background);
        ${Label} {
            color: var(--gameday-button-hollow-text);
        }
        ${IconWrapper} {
            fill: var(--gameday-button-hollow-border);
            path {
                fill: var(--gameday-button-hollow-border);
            }
        }
    }
`;

export const VoteLink = styled(BaseVoteLink).attrs(({ onClick }) => ({
    handlers: { onClick }
}))`
    width: 100%;
    max-width: 270px;
    margin: 0 auto;
    height: 45px;
`;

export const Leaderboard = styled.a`
    display: block;
    width: max-content;
    margin: var(--gameday-section-margin-sm) 0 0 auto;
    color: var(--gameday-button-hollow-text) !important;
    font-family: var(--gameday-primary-font);
    font-weight: 700;
    font-size: 14px;
    text-decoration: none;
`;

export const Icon = styled(IconPattern)`
    margin-left: 5px;
    fill: var(--gameday-button-hollow-text);
    path {
        fill: var(--gameday-button-hollow-text);
    }
`;
export const WinnerWrapper = styled.div`
    min-width: 130px;
    background-color: var(--gameday-background-color);
    border-radius: 8px;
    padding: 16px;
    position: relative;
    box-sizing: content-box;
    align-items: center;
    justify-content: flex-start;
    display: flex;
    flex-direction: column;
    margin: 0 16px;
    :first-child {
        margin-left: 0px;
    }
    :only-child {
        width: 100%;
        margin: 0;
    }
    @media (max-width: ${SM_REG_MAX}) {
        box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.08);
        margin: 0px 8px 3px;
    }
`;

export const WinnersContainer = styled.div`
    display: flex;
    align-items: stretch;
    justify-content: center;
    @media (min-width: ${SM_REG_MAX}) {
        ${WinnerWrapper} {
            :not(:last-child){
                :after {
                    height: calc(100% - 60px);
                    background: var(--gameday-divider-color);
                    width: 1px;
                    content: '';
                    position: absolute;
                    top: 30px;
                    right: -16px;
                }
            }
        }
    }
`;

export const WinnerThumbnailWrapper = styled.div`
    display: flex;
    justify-content: center;
    position: relative;
    min-width: 175px;
    height: 80px; // 68px (headshot height) + 24px (badge height) - 12px (overlap)
`;

export const WinnerPhoto = styled.div`
    border-radius: 64px;
    overflow: hidden;
    width: 64px;
    height: 64px;
    outline: ${({ type }) => type === ACTION ? '2px solid var(--gameday-potg-accent-color);' : ''};
    outline-offset: ${({ type }) => type === ACTION ? '2px' : ''};
    box-sizing: border-box;
    position: absolute;
    top: 4px;
`;

export const WinnerBadge = styled.div`
    padding: 4px 8px;
    position: absolute;
    bottom: 0;
    background-color: var(--gameday-potg-badge-background-color);
    color: var(--gameday-potg-badge-text-color);
    border-radius: 4px;
    border: 2px solid var(--gameday-background-color);
    text-align: center;
    max-height: 24px;
    font-family: var(--gameday-primary-font);
    font-size: 12px;
    line-height: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.96px;
    box-sizing: content-box;
    max-width: 100%;
`;

export const WinnerDescriptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 114px;
`;

export const WinnerName = styled.div`
    color: var(--gameday-primary-font-color);
    text-align: center;
    font-family: var(--gameday-primary-font);
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
`;

export const WinnerStats = styled.div`
    color: var(--gameday-secondary-font-color);
    text-align: center;
    font-family: var(--gameday-primary-font);
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 18px;
    white-space: pre-line;
`;

export const PlayerofTheGameSkeleton = styled(Skeleton)`
    margin: auto;
`;
